import React from 'react';
import PropTypes from 'prop-types';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Button, Text, Title, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import classNames from 'classnames';
import styles from './campaign-highlight.module.less';
import SegmentImpression from '../../segment-impression';
import useAnalytics from '~/client/lib/segment/useAnalytics';
import { HOMEPAGE_SEGMENT_INSTRUMENTATION } from '~/client/lib/analytics-constants';
import NewFeatureTag from '../announcement-banner/new-feature-tag/new-feature-tag';

const CampaignHighlight = ({
  backgroundColor,
  backgroundColorMobile,
  title,
  titleEnd,
  titlePreColor,
  titleEndColor,
  subtitle,
  ctaButtonText,
  ctaLinkUrl,
  visualDesktopBackgroundImageUrl,
  visualMobileBackgroundImageUrl,
  visualBackgroundImageAlt,
  elementImpressionEntityName,
  elementImpressionSectionName,
  trkQueryParam,
}) => {
  const backgroundImageStyleDesktop = {
    backgroundColor: `${backgroundColor}`,
    backgroundImage: `url(${visualDesktopBackgroundImageUrl})`,
    backgroundSize: `cover`,
  };

  const backgroundImageStyleMobile = {
    backgroundColor: `${backgroundColorMobile}`,
  };

  const titlePreStyle = {
    color: `${titlePreColor}`,
  };

  const titleEndStyle = {
    color: `${titleEndColor}`,
  };

  const { trackSegmentTrackEvent } = useAnalytics();
  const handleCampaignLinkClick = () => {
    trackSegmentTrackEvent(
      'Element Interaction',
      HOMEPAGE_SEGMENT_INSTRUMENTATION.campaign
    );
  };

  return (
    <SegmentImpression
      eventName="Element Impression"
      eventProps={{
        entity_name: HOMEPAGE_SEGMENT_INSTRUMENTATION.campaign.sub_location,
        location: HOMEPAGE_SEGMENT_INSTRUMENTATION.campaign.location,
      }}
    >
      <Impression
        entityName={elementImpressionEntityName}
        payload={{ section_name: elementImpressionSectionName }}
      >
        <div className={styles.campaignHighlight}>
          <div
            className={styles.campaignHighlightVisualContainerMobile}
            style={backgroundImageStyleMobile}
          >
            <Link
              href={`${ctaLinkUrl}?trk=${trkQueryParam}`}
              onClick={handleCampaignLinkClick}
            >
              <Image
                backgroundColor={backgroundColorMobile}
                src={visualMobileBackgroundImageUrl}
                alt={visualBackgroundImageAlt}
                imageClassName={styles.campaignHighlightVisualImageMobile}
                width="100%"
                naturalWidth="1170"
                naturalHeight="822"
                lazy
              />
            </Link>
          </div>
          <div
            className={styles.campaignHighlightCta}
            style={{ backgroundColor }}
          >
            <NewFeatureTag
              color="#008254"
              classNames={classNames(['boa-ribbon'])}
            />
            <Title size="small" className={styles.campaignHighlightCtaTitle}>
              <span
                className={styles.campaignHighlightCtaTitlePre}
                style={titlePreStyle}
              >
                {title}
              </span>
              <span
                className={styles.campaignHighlightCtaTitleEnd}
                style={titleEndStyle}
              >
                {'\u00A0'}
                {'\u00A0'}
                {titleEnd}
              </span>
            </Title>
            <div className={styles.campaignHighlightCtaText}>
              <Text>{subtitle}</Text>
            </div>
            <Button
              href={`${ctaLinkUrl}?trk=${trkQueryParam}`}
              className={styles.campaignHighlightCtaButton}
              onClick={handleCampaignLinkClick}
            >
              {ctaButtonText}
            </Button>
          </div>
          <Link
            href={`${ctaLinkUrl}?trk=${trkQueryParam}`}
            onClick={handleCampaignLinkClick}
          >
            <div
              className={styles.campaignHighlightVisualContainerDesktop}
              style={backgroundImageStyleDesktop}
            />
          </Link>
        </div>
      </Impression>
    </SegmentImpression>
  );
};

CampaignHighlight.propTypes = {
  backgroundColor: PropTypes.string,
  backgroundColorMobile: PropTypes.string,
  title: PropTypes.string,
  titleEnd: PropTypes.string,
  titlePreColor: PropTypes.string,
  titleEndColor: PropTypes.string,
  subtitle: PropTypes.string,
  ctaButtonText: PropTypes.string,
  ctaLinkUrl: PropTypes.string,
  visualDesktopBackgroundImageUrl: PropTypes.string,
  visualMobileBackgroundImageUrl: PropTypes.string,
  visualBackgroundImageAlt: PropTypes.string,
  elementImpressionEntityName: PropTypes.string,
  elementImpressionSectionName: PropTypes.string,
  trkQueryParam: PropTypes.string,
};

CampaignHighlight.displayName = 'CampaignHighlight';

export default CampaignHighlight;
